import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  Collapse,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Cancel as CancelIcon,
  HolidayVillage as HolidayIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import HolidayDialog from './Holiday.tsx';
import { Day, Subtopic } from '../../../types/curriculum.types.ts';

interface DayItemProps {
  day: Day;
  dayIndex: number;
  availableSubtopics: Subtopic[];
  onUpdateDay: (id: string, patch: Partial<Day>) => void;
  onDeleteDay: (id: string) => void;
}

const DAY_COLORS: Record<Day['dayOfWeek'], string> = {
  Monday: '#EFF6FF',
  Tuesday: '#F0FDF4',
  Wednesday: '#FEFCE8',
  Thursday: '#FDF2F8',
  Friday: '#F3E8FF',
  Saturday: '#FEF2F2',
  Sunday: '#F8FAFC',
};

const DAY_LABELS: Record<Day['dayOfWeek'], string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

const HOLIDAY_TYPE_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  weekly_off:  { bg: '#FEF3C7', color: '#92400E', label: 'Weekly Off' },
  government:  { bg: '#DBEAFE', color: '#1E40AF', label: 'Govt. Holiday' },
  school:      { bg: '#D1FAE5', color: '#065F46', label: 'School Holiday' },
  festival:    { bg: '#FCE7F3', color: '#9D174D', label: 'Festival' },
  exam:        { bg: '#E0E7FF', color: '#3730A3', label: 'Exam' },
  emergency:   { bg: '#FEE2E2', color: '#991B1B', label: 'Emergency' },
  other:       { bg: '#F3F4F6', color: '#374151', label: 'Other' },
};

export default function DayItem({ day, dayIndex, availableSubtopics, onUpdateDay, onDeleteDay }: DayItemProps) {
  const [expanded, setExpanded] = useState(true);
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);

  const [newObjective, setNewObjective] = useState('');
  const [newActivity, setNewActivity] = useState('');
  const [newMaterial, setNewMaterial] = useState('');
  const [showObjectiveInput, setShowObjectiveInput] = useState(false);
  const [showActivityInput, setShowActivityInput] = useState(false);
  const [showMaterialInput, setShowMaterialInput] = useState(false);

  const isWeekend = day.dayOfWeek === 'Saturday' || day.dayOfWeek === 'Sunday';
  const isSunday = day.dayOfWeek === 'Sunday';

  const getHolidayLabel = () => {
    if (!day.isHoliday || !day.holidayType) return null;
    return HOLIDAY_TYPE_COLORS[day.holidayType]?.label || 'Holiday';
  };

  const handleSubtopicChange = (subtopicId: string) => {
    const subtopic = availableSubtopics.find(st => st.id === subtopicId);
    if (subtopic) {
      onUpdateDay(day.id, { subtopicId, objectives: [subtopic.name] });
    }
  };

  const handleAdd = (
    type: 'objective' | 'activity' | 'material',
    value: string,
    setter: (v: string) => void,
    showSetter: (v: boolean) => void
  ) => {
    if (!value.trim()) return;
    const key = type === 'objective' ? 'objectives' : type === 'activity' ? 'activities' : 'materials';
    onUpdateDay(day.id, { [key]: [...(day[key as keyof Day] as string[]), value.trim()] });
    setter('');
    showSetter(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent, handler: () => void) => {
    if (e.key === 'Enter') handler();
    if (e.key === 'Escape') {
      setShowObjectiveInput(false);
      setShowActivityInput(false);
      setShowMaterialInput(false);
      setNewObjective('');
      setNewActivity('');
      setNewMaterial('');
    }
  };

  const removeItem = (field: 'objectives' | 'activities' | 'materials', idx: number) => {
    const arr = [...(day[field] as string[])];
    arr.splice(idx, 1);
    onUpdateDay(day.id, { [field]: arr });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        border: '1px solid',
        borderColor: day.isHoliday ? '#FCA5A5' : '#E2E8F0',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: day.isHoliday ? '#FEF2F2' : (isWeekend ? '#FAFAFA' : 'white'),
        opacity: day.isHoliday ? 0.85 : 1,
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5, py: 1.25,
          display: 'flex', alignItems: 'center', gap: 1.5,
          bgcolor: day.isHoliday ? '#FEE2E2' : (DAY_COLORS[day.dayOfWeek] || '#F8FAFC'),
          borderBottom: '1px solid #E2E8F0',
          flexWrap: 'wrap',
        }}
      >
        <Chip
          label={DAY_LABELS[day.dayOfWeek]}
          size="medium"
          sx={{
            fontSize: '0.75rem', height: 26, fontWeight: 700,
            bgcolor: day.isHoliday ? '#EF4444' : (isSunday ? '#EF4444' : (isWeekend ? '#F59E0B' : '#3B82F6')),
            color: 'white',
          }}
        />

        {day.customTitle && !day.isHoliday && (
          <Chip label={day.customTitle} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#E0E7FF', color: '#3730A3' }} />
        )}

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', flex: 1 }}>
          {day.isReviewDay && !day.isHoliday && (
            <Chip label="📝 Review" size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E' }} />
          )}
          {day.isHoliday && (
            <Chip
              label={`🎉 ${getHolidayLabel() || 'Holiday'}${day.holidayReason ? `: ${day.holidayReason}` : ''}`}
              size="small"
              sx={{
                fontSize: '0.65rem', height: 24, maxWidth: 280,
                bgcolor: day.holidayType ? HOLIDAY_TYPE_COLORS[day.holidayType]?.bg || '#FEE2E2' : '#FEE2E2',
                color: day.holidayType ? HOLIDAY_TYPE_COLORS[day.holidayType]?.color || '#991B1B' : '#991B1B',
              }}
            />
          )}
          {day.objectives.length > 0 && (
            <Chip label={`${day.objectives.length} objectives`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#D1FAE5', color: '#065F46' }} />
          )}
          {day.activities.length > 0 && (
            <Chip label={`${day.activities.length} activities`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#E0E7FF', color: '#3730A3' }} />
          )}
          {day.materials.length > 0 && (
            <Chip label={`${day.materials.length} materials`} size="small" sx={{ fontSize: '0.65rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
          <Tooltip title="Mark as Holiday" arrow>
            <span>
              <IconButton size="small" color="primary" onClick={() => setShowHolidayDialog(true)} sx={{ p: 0.5 }} disabled={day.isHoliday}>
                <HolidayIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
          {day.isHoliday && (
            <Tooltip title="Remove Holiday" arrow>
              <IconButton
                size="small" color="warning"
                onClick={() => onUpdateDay(day.id, { isHoliday: false, holidayType: undefined, holidayReason: '', isReviewDay: day.dayOfWeek === 'Sunday' })}
                sx={{ p: 0.5 }}
              >
                <CancelIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={expanded ? 'Collapse' : 'Expand'} arrow>
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: 0.5 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 20 }} /> : <ExpandMoreIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Day" arrow>
            <IconButton size="small" color="error" onClick={() => onDeleteDay(day.id)} sx={{ p: 0.5 }}>
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={1.5}>

            {/* Subtopic Dropdown */}
            {!day.isHoliday && availableSubtopics.length > 0 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem' }}>Select Subtopic</InputLabel>
                  <Select
                    value={day.subtopicId || ''}
                    onChange={(e) => handleSubtopicChange(e.target.value)}
                    label="Select Subtopic"
                    sx={{ fontSize: '0.8rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>Choose a subtopic...</em></MenuItem>
                    {availableSubtopics.map((st) => (
                      <MenuItem key={st.id} value={st.id} sx={{ fontSize: '0.8rem' }}>
                        {st.name} {st.marks ? `(${st.marks} marks)` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#94A3B8', mt: 0.5, display: 'block' }}>
                  Select a subtopic for this day's learning objective
                </Typography>
              </Grid>
            )}

            {/* Objectives */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                Additional Objectives ({day.objectives.length})
                <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => { setShowObjectiveInput(true); setShowActivityInput(false); setShowMaterialInput(false); }}
                  sx={{ fontSize: '0.75rem', minHeight: 28, py: 0, textTransform: 'none' }}
                  disabled={day.isHoliday}
                >Add</Button>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {day.objectives.map((obj, idx) => (
                  <Chip key={idx} label={obj} size="small" sx={{ fontSize: '0.75rem', height: 28 }}
                    onDelete={() => removeItem('objectives', idx)} disabled={day.isHoliday} />
                ))}
              </Box>
              {showObjectiveInput && !day.isHoliday && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <TextField size="small" value={newObjective} onChange={(e) => setNewObjective(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, () => handleAdd('objective', newObjective, setNewObjective, setShowObjectiveInput))}
                    placeholder="Enter objective..." sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} variant="outlined" autoFocus />
                  <IconButton size="small" color="success" onClick={() => handleAdd('objective', newObjective, setNewObjective, setShowObjectiveInput)} sx={{ p: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setShowObjectiveInput(false); setNewObjective(''); }} sx={{ p: 0.5 }}>
                    <CancelIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              )}
            </Grid>

            {/* Activities */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                Activities ({day.activities.length})
                <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => { setShowActivityInput(true); setShowObjectiveInput(false); setShowMaterialInput(false); }}
                  sx={{ fontSize: '0.75rem', minHeight: 28, py: 0, textTransform: 'none' }}
                  disabled={day.isHoliday}
                >Add</Button>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {day.activities.map((act, idx) => (
                  <Chip key={idx} label={act} size="small" sx={{ fontSize: '0.75rem', height: 28, bgcolor: '#E0E7FF' }}
                    onDelete={() => removeItem('activities', idx)} disabled={day.isHoliday} />
                ))}
              </Box>
              {showActivityInput && !day.isHoliday && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <TextField size="small" value={newActivity} onChange={(e) => setNewActivity(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, () => handleAdd('activity', newActivity, setNewActivity, setShowActivityInput))}
                    placeholder="Enter activity..." sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} variant="outlined" autoFocus />
                  <IconButton size="small" color="success" onClick={() => handleAdd('activity', newActivity, setNewActivity, setShowActivityInput)} sx={{ p: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setShowActivityInput(false); setNewActivity(''); }} sx={{ p: 0.5 }}>
                    <CancelIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              )}
            </Grid>

            {/* Materials */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                Materials/Resources ({day.materials.length})
                <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => { setShowMaterialInput(true); setShowObjectiveInput(false); setShowActivityInput(false); }}
                  sx={{ fontSize: '0.75rem', minHeight: 28, py: 0, textTransform: 'none' }}
                  disabled={day.isHoliday}
                >Add</Button>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {day.materials.map((mat, idx) => (
                  <Chip key={idx} label={mat} size="small" sx={{ fontSize: '0.75rem', height: 28, bgcolor: '#D1FAE5' }}
                    onDelete={() => removeItem('materials', idx)} disabled={day.isHoliday} />
                ))}
              </Box>
              {showMaterialInput && !day.isHoliday && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <TextField size="small" value={newMaterial} onChange={(e) => setNewMaterial(e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, () => handleAdd('material', newMaterial, setNewMaterial, setShowMaterialInput))}
                    placeholder="Enter material/resource..." sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }} variant="outlined" autoFocus />
                  <IconButton size="small" color="success" onClick={() => handleAdd('material', newMaterial, setNewMaterial, setShowMaterialInput)} sx={{ p: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setShowMaterialInput(false); setNewMaterial(''); }} sx={{ p: 0.5 }}>
                    <CancelIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>
              )}
            </Grid>

            {/* Homework */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569', mb: 0.5 }}>Homework</Typography>
              <TextField size="small" fullWidth value={day.homework || ''}
                onChange={(e) => onUpdateDay(day.id, { homework: e.target.value })}
                placeholder="Assign homework..." sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.5 } }}
                variant="outlined" disabled={day.isHoliday} />
            </Grid>

            {/* Custom Title & Review Day */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="Custom Title" value={day.customTitle || ''}
                  onChange={(e) => onUpdateDay(day.id, { customTitle: e.target.value })}
                  placeholder="e.g., Revision Day, Lab Day..."
                  sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.85rem' } }} variant="outlined" disabled={day.isHoliday} />
                <FormControlLabel
                  control={<Checkbox checked={day.isReviewDay} onChange={(e) => onUpdateDay(day.id, { isReviewDay: e.target.checked })} size="medium" disabled={day.isHoliday} />}
                  label={<Typography variant="body1" sx={{ fontSize: '0.8rem' }}>Review Day</Typography>}
                />
              </Box>
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#475569', mb: 0.5 }}>Notes</Typography>
              <TextField size="small" fullWidth multiline rows={1} value={day.notes || ''}
                onChange={(e) => onUpdateDay(day.id, { notes: e.target.value })}
                placeholder="Additional notes..." sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} variant="outlined" />
            </Grid>

          </Grid>
        </Box>
      </Collapse>

      <HolidayDialog
        open={showHolidayDialog}
        onClose={() => setShowHolidayDialog(false)}
        onSave={(holidayType, reason) => {
          onUpdateDay(day.id, { isHoliday: true, holidayType: holidayType as any, holidayReason: reason || 'Holiday', isReviewDay: false });
          setShowHolidayDialog(false);
        }}
        existingHolidayType={day.holidayType}
        existingReason={day.holidayReason}
      />
    </Paper>
  );
}